<template>
  <div class="custom-ion-checkbox" :class="'size-' + size">
    <CustomIonLabel
      v-if="inputLabel"
      :inputLabel="inputLabel"
      :inputId="inputId"
      :isMandatory="isMandatory"
      :size="size"
    />
    <CustomIonSearchbar
      v-if="isSearchable && !isDisabled"
      ref="searchbarRef"
      :placeholder="`Search for a ${searchField}`"
      @onSearch="onSearch"
      :key="searchReloadKey"
      class="mb-3"
    />
    <div v-if="!hideSelectAll" class="row-center-gap-2 px-2 py-1 mb-1">
      <IonCheckbox
        :indeterminate="isCheckAllIndeterminate"
        :checked="isAllChecked"
        :disabled="isDisabled"
        :label-placement="labelPlacement"
        @ionChange="onCheckAllChange"
        class="select-all-checkbox select-all-meta font-normal"
        :class="isAllChecked || isCheckAllIndeterminate ? 'color-primary' : 'color-text'"
      >
        Select All
      </IonCheckbox>
      <span
        class="pill-badge pill-badge-primary"
        :class="{ invisible: checkedValues.length === 0 }"
      >
        {{ checkedValues.length }} / {{ totalCheckboxItems }}
      </span>
    </div>
    <div v-if="maxItems !== undefined" class="text-xxs color-secondary mb-1">
      Select up to {{ maxItems }}
    </div>
    <div
      :class="
        isVerticalMode
          ? 'flex flex-col gap-1'
          : 'flex flex-col gap-2 xs:flex-row xs:flex-wrap xs:gap-1'
      "
    >
      <template v-if="isVerticalMode">
        <IonCheckbox
          v-for="checkboxItem in displayedItems"
          :disabled="checkboxItem.IsDisabled || isDisabled"
          :key="checkboxItem.Id"
          :label-placement="labelPlacement"
          :value="checkboxItem.Id"
          :checked="isItemSelected(checkboxItem)"
          @ionChange="onCheckedItemsChange"
          class="flex flex-col justify-center w-full px-2 border-b control-list-row"
          :class="
            isItemSelected(checkboxItem)
              ? 'bg-color-primary-light border-color-primary color-primary'
              : 'color-text'
          "
        >
          <span class="flex items-center gap-1.5 w-full">
            <i
              v-if="checkboxItem.IconName"
              :class="`ti ti-${checkboxItem.IconName}`"
              class="shrink-0"
              aria-hidden="true"
            ></i>
            <span class="flex-1 min-w-0 text-left">{{ checkboxItem.Name }}</span>
            <i
              v-show="isItemSelected(checkboxItem)"
              class="ti ti-check shrink-0"
              aria-hidden="true"
            ></i>
          </span>
        </IonCheckbox>
        <LazyLoadingViewer
          v-if="hasMore"
          :key="`load-more-${displayedItemCount}`"
          @onVisible="loadMore"
        >
          <template #content>
            <span />
          </template>
          <template #placeholder>
            <SkeletonShimmerBase>
              <SkeletonItem variant="rect" width="100%" height="var(--size-touch)" />
            </SkeletonShimmerBase>
          </template>
        </LazyLoadingViewer>
      </template>
      <template v-else>
        <IonCheckbox
          v-show="checkboxItem.IsVisible"
          v-for="checkboxItem in processedCheckboxItems"
          :disabled="checkboxItem.IsDisabled || isDisabled"
          :key="checkboxItem.Id"
          :label-placement="labelPlacement"
          :value="checkboxItem.Id"
          :checked="isItemSelected(checkboxItem)"
          @ionChange="onCheckedItemsChange"
          class="toggle-pill-base font-normal max-w-full min-w-0"
          :class="checkboxItem.IsDisabled || isDisabled ? 'opacity-75' : ''"
        >
          <span class="flex items-center gap-1.5 w-full">
            <i
              v-if="checkboxItem.IconName"
              :class="`ti ti-${checkboxItem.IconName}`"
              class="shrink-0"
              aria-hidden="true"
            ></i>
            <span class="flex-1 min-w-0 text-left">{{ checkboxItem.Name }}</span>
            <i
              v-show="isItemSelected(checkboxItem)"
              class="ti ti-check shrink-0"
              aria-hidden="true"
            ></i>
          </span>
        </IonCheckbox>
      </template>
    </div>
    <NoRecordFound v-if="isAllItemsHidden" />
    <div v-if="isFormField" class="validation-slot">
      <Field v-model="checkedValues" :id="inputId" :name="inputId" hidden />
      <ErrorMessage :name="inputId" class="validation-message" />
    </div>
  </div>
</template>

<script lang="ts">
import CustomIonLabel from "@/components/inputs/CustomIonLabel.vue";
import CustomIonSearchbar from "@/components/ionic/CustomIonSearchbar.vue";
import NoRecordFound from "@/components/states/NoRecordFound.vue";
import LazyLoadingViewer from "@/components/widgets/LazyLoadingViewer.vue";
import SkeletonItem from "@/components/skeletons/SkeletonItem.vue";
import SkeletonShimmerBase from "@/components/skeletons/SkeletonShimmerBase.vue";
import {
  handleCheckboxCheckAllChange,
  handleCheckboxSearch,
  isCheckboxCheckAll,
  isCheckboxCheckAllIndeterminate,
  setCheckboxItemsDisabled,
} from "@/methods/input";
import { deepCopyObject } from "@/methods/utils";
import type { ComponentSizeType } from "@/types/componentType";
import { InputOption } from "@/types/global";
import { CheckboxCustomEvent, IonCheckbox } from "@ionic/vue";
import { ErrorMessage, Field } from "vee-validate";
import { PropType, computed, defineComponent, onBeforeMount, ref } from "vue";

export default defineComponent({
  name: "CustomIonCheckbox",
  components: {
    CustomIonSearchbar,
    CustomIonLabel,
    IonCheckbox,
    NoRecordFound,
    LazyLoadingViewer,
    SkeletonShimmerBase,
    SkeletonItem,
    ErrorMessage,
    Field,
  },
  props: {
    inputId: { type: String, required: true },
    checkboxItems: { type: Array as PropType<Array<InputOption>> },
    initialValue: { type: Array as PropType<Array<number | string>>, default: [] },
    maxItems: { type: Number, default: undefined },
    isSearchable: { type: Boolean, default: false },
    searchField: { type: String, default: "value" },
    inputLabel: { type: String, required: false },
    isMandatory: { type: Boolean, default: false },
    isDisabled: { type: Boolean, default: false },
    isFormField: { type: Boolean, default: false },
    isVerticalMode: { type: Boolean, default: false },
    hideSelectAll: { type: Boolean, default: false },
    size: { type: String as PropType<ComponentSizeType>, default: "default" },
    labelPlacement: {
      type: String as PropType<"end" | "fixed" | "start">,
      default: "start",
    },
  },
  setup(props, { emit }) {
    const CHUNK_SIZE = 50;
    const checkedValues = ref<Array<number | string>>([...props.initialValue]);
    const isAllChecked = ref(false);
    const isCheckAllIndeterminate = ref(false);
    const searchReloadKey = ref(0);
    const processedCheckboxItems = ref<Array<InputOption>>();
    const totalCheckboxItems = props.checkboxItems ? props.checkboxItems.length : 0;
    const searchedValue = ref("");
    const displayedItemCount = ref(CHUNK_SIZE);
    const searchbarRef = ref<InstanceType<typeof CustomIonSearchbar> | null>(null);

    const isAllItemsHidden = computed(() => {
      if (!(processedCheckboxItems.value && processedCheckboxItems.value.length > 0)) {
        return true;
      }
      return !processedCheckboxItems.value.some((item) => item.IsVisible);
    });

    const displayedItems = computed(() => {
      if (!processedCheckboxItems.value) {
        return [];
      }
      if (searchedValue.value) {
        return processedCheckboxItems.value.filter((item) => item.IsVisible);
      }
      return processedCheckboxItems.value.slice(0, displayedItemCount.value);
    });

    const hasMore = computed(() => {
      return (
        !searchedValue.value &&
        displayedItemCount.value < (processedCheckboxItems.value?.length ?? 0)
      );
    });

    function loadMore() {
      displayedItemCount.value += CHUNK_SIZE;
    }

    onBeforeMount(() => {
      processedCheckboxItems.value = props.checkboxItems
        ? (deepCopyObject(props.checkboxItems) as Array<InputOption>)
        : [];
      handleCheckboxSearch(processedCheckboxItems.value, "");
      if (props.maxItems !== undefined) {
        setCheckboxItemsDisabled(processedCheckboxItems.value, checkedValues.value, props.maxItems);
      }
      isAllChecked.value = isCheckboxCheckAll(
        processedCheckboxItems.value,
        checkedValues.value,
        totalCheckboxItems,
      );
      isCheckAllIndeterminate.value = isCheckboxCheckAllIndeterminate(
        processedCheckboxItems.value,
        checkedValues.value,
        totalCheckboxItems,
      );
    });

    function onCheckedItemsChange(event: CheckboxCustomEvent) {
      const itemId = event.detail.value;
      if (!checkedValues.value.includes(itemId)) {
        checkedValues.value = [...checkedValues.value, itemId];
      } else {
        checkedValues.value = checkedValues.value.filter(
          (checkedValue: number | string) => checkedValue !== itemId,
        );
      }
      isAllChecked.value = isCheckboxCheckAll(
        processedCheckboxItems.value,
        checkedValues.value,
        totalCheckboxItems,
      );
      isCheckAllIndeterminate.value = isCheckboxCheckAllIndeterminate(
        processedCheckboxItems.value,
        checkedValues.value,
        totalCheckboxItems,
      );
      setCheckboxItemsDisabled(processedCheckboxItems.value, checkedValues.value, props.maxItems);
      searchReloadKey.value += 1;
      emit("onChange", checkedValues.value);
    }

    function onCheckAllChange(event: CheckboxCustomEvent) {
      isAllChecked.value = event.detail.checked;
      const updatedValues = handleCheckboxCheckAllChange(
        processedCheckboxItems.value,
        checkedValues.value,
        isAllChecked.value,
        totalCheckboxItems,
        props.maxItems,
      );
      checkedValues.value = updatedValues.checkedValues;
      isCheckAllIndeterminate.value = updatedValues.isCheckAllIndeterminate;
      setCheckboxItemsDisabled(processedCheckboxItems.value, checkedValues.value, props.maxItems);
      emit("onChange", checkedValues.value);
      onSearch("");
    }

    function onSearch(value: string) {
      if (!value && searchedValue.value) {
        displayedItemCount.value = CHUNK_SIZE;
      }
      searchedValue.value = value;
      handleCheckboxSearch(processedCheckboxItems.value, value);
    }

    function isItemSelected(item: InputOption) {
      return checkedValues.value.includes(item.Id);
    }

    // Called by an owning overlay (CustomIonSelect) once its enter transition settles, so the
    // search field is ready to type into. No-ops when the searchbar isn't rendered.
    async function focusSearch(): Promise<void> {
      await searchbarRef.value?.setFocus();
    }

    return {
      processedCheckboxItems,
      displayedItems,
      displayedItemCount,
      hasMore,
      checkedValues,
      isAllChecked,
      isCheckAllIndeterminate,
      isAllItemsHidden,
      totalCheckboxItems,
      searchReloadKey,
      searchbarRef,
      onCheckedItemsChange,
      onCheckAllChange,
      onSearch,
      loadMore,
      isItemSelected,
      focusSearch,
    };
  },
});
</script>

<style>
ion-checkbox {
  --size: var(--font-size-m);
  --border-color: var(--color-medium);
  --checkbox-background-checked: var(--color-primary);
  --border-color-checked: var(--color-primary);
}
ion-checkbox::part(container) {
  border-radius: var(--control-radius);
}
ion-checkbox::part(label) {
  margin-inline-start: 6px;
}
.custom-ion-checkbox ion-checkbox.toggle-pill-base,
.custom-ion-checkbox ion-checkbox.control-list-row {
  --checkbox-background: transparent;
  --checkbox-background-checked: transparent;
  --border-color: transparent;
  --border-color-checked: transparent;
}
/* Hide the native checkbox box — the slotted Tabler "ti-check" renders the tick instead */
.custom-ion-checkbox ion-checkbox.toggle-pill-base::part(container),
.custom-ion-checkbox ion-checkbox.control-list-row::part(container) {
  display: none;
}
.custom-ion-checkbox ion-checkbox.toggle-pill-base {
  --toggle-pill-height: var(--control-height);
  --toggle-pill-padding: var(--control-pad-y-small) var(--pill-pad-x);
}
.custom-ion-checkbox ion-checkbox.toggle-pill-base::part(label) {
  font-size: var(--control-font);
  white-space: normal;
  overflow-wrap: anywhere;
}
.custom-ion-checkbox ion-checkbox.toggle-pill-base.checkbox-checked {
  border-color: var(--color-primary) !important;
  background-color: var(--color-primary-bg-light) !important;
  color: var(--color-primary) !important;
}
.custom-ion-checkbox.size-small {
  ion-checkbox {
    --size: var(--font-size-s);
  }
  ion-checkbox.select-all-checkbox {
    --size: var(--font-size-xs);
  }
  ion-checkbox.select-all-checkbox::part(label) {
    font-size: var(--text-xxs);
  }
}
.custom-ion-checkbox.size-large {
  ion-checkbox {
    --size: var(--font-size-l);
  }
  ion-checkbox.select-all-checkbox {
    --size: var(--font-size-m);
  }
  ion-checkbox.select-all-checkbox::part(label) {
    font-size: var(--control-font-default);
  }
}
</style>
